import axios from 'axios';

const instance = axios.create({
    baseURL: process.env.VUE_APP_BACKEND_API,
    headers: {
        'Content-Type': 'application/json',
    },
});

// POST /auth/reissue (login-controller, 토큰 재발행)
// body 는 TokenRequestDto = { grantType, accessToken, accessTokenExpiresIn, refreshToken }
// 이는 로그인이 내려주는 TokenInfo 와 필드가 동일하므로 저장된 토큰을 그대로 보내면 됨.
const REISSUE_PATH = '/auth/reissue';

// 재발급 요청은 instance 가 아닌 raw axios 로 보냄.
// instance 로 보내면 재발급 자체가 401 일 때 인터셉터가 다시 재발급을 부르는 무한 루프가 됨.
function requestReissue(token) {
    return axios.post(
        `${process.env.VUE_APP_BACKEND_API}${REISSUE_PATH}`,
        JSON.stringify({
            grantType: token.grantType,
            accessToken: token.accessToken,
            accessTokenExpiresIn: token.accessTokenExpiresIn,
            refreshToken: token.refreshToken,
        }),
        { headers: { 'Content-Type': 'application/json' } }
    ).then(response => {
        // 서버가 모든 응답을 { statusCode, timestamp, response: ... } 로 감쌈.
        // 재발급은 그 안에 TokenInfo 가 바로 들어옴 (로그인처럼 token 으로 한 번 더 감싸지 않음).
        const payload = response.data?.response ?? response.data;
        const reissued = payload?.accessToken ? payload : payload?.token;
        if (!reissued?.accessToken) {
            throw new Error('reissue-response-malformed');
        }
        return reissued;
    });
}

export function setupInterceptors(store, router) {
    instance.interceptors.request.use(function (config) {
        if (store.state.login.token) {
            config.headers['Authorization'] = `${store.state.login.token.grantType} ${store.state.login.token.accessToken}`;
        }
        return config;
    });

    // 여러 요청이 동시에 401 을 받아도 재발급은 한 번만 타도록 공유.
    let pendingReissue = null;

    const endSession = () => {
        store.commit('login/setUser', { token: null, nickname: '' });
        if (router.currentRoute.value.path !== '/login') {
            router.replace('/login');
        }
    };

    instance.interceptors.response.use(
        response => response,
        async error => {
            const config = error.config;

            // 네트워크 에러(응답 없음), 401 이 아닌 에러, 이미 한 번 재시도한 요청은 그대로 통과
            if (error.response?.status !== 401 || !config || config._retried) {
                return Promise.reject(error);
            }

            const token = store.state.login.token;
            if (!token?.refreshToken) {
                // 재발급할 수단이 없음 — 죽은 세션이므로 정리하고 로그인으로
                endSession();
                return Promise.reject(error);
            }

            config._retried = true;

            try {
                if (!pendingReissue) {
                    pendingReissue = requestReissue(token)
                        .then(reissued => {
                            store.commit('login/setUser', {
                                token: reissued,
                                nickname: store.state.login.nickname,
                            });
                            return reissued;
                        })
                        .finally(() => { pendingReissue = null; });
                }
                const reissued = await pendingReissue;

                config.headers['Authorization'] = `${reissued.grantType} ${reissued.accessToken}`;
                return instance(config);
            } catch (reissueError) {
                // 리프레시 토큰까지 만료/무효 — 재로그인 외에는 방법이 없음
                console.error('토큰 재발급에 실패했습니다. 다시 로그인해주세요.', reissueError);
                endSession();
                return Promise.reject(error);
            }
        }
    );
}

export default instance;
